import { SectionFooter } from "@admin-ui/components/application/section-footers/section-footer";
import { SectionHeader } from "@admin-ui/components/application/section-headers/section-headers";
import { SectionLabel } from "@admin-ui/components/application/section-headers/section-label";
import { InputDate } from "@admin-ui/components/base/input/input-date";
import { InputNumber } from "@admin-ui/components/base/input/input-number";
import { RichTextEditor } from "@admin-ui/components/base/rich-text-editor";
import { MultiSelect } from "@admin-ui/components/base/select/multi-select";
import { Select } from "@admin-ui/components/base/select/select";
import { Tab, TabList, TabPanel, Tabs } from "@admin-ui/components/primitives/application/tabs/tabs";
import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import { Input } from "@admin-ui/components/primitives/base/input/input";
import { Toggle } from "@admin-ui/components/primitives/base/toggle/toggle";
import { type DateValue, getLocalTimeZone, today } from "@internationalized/date";
import type { AdminProgramsLocation, AdminProgramsSession, ProgramFormat } from "@travily/types/models";
import { type FormEvent, type Key, type ReactNode, useMemo, useState } from "react";
import { MapboxSearch, type MapboxSearchResult } from "@/components/admin/MapboxSearch";
import { AiSuggestedBadge } from "@/components/admin/programs/AiSuggestedBadge";
import { firstError } from "@/lib/forms/first-error";
import { humanize } from "@/lib/humanize";

export interface ProgramDetailsValues {
  name: string;
  tagline: string;
  description: string;
  drop_off_available: boolean;
  community_program: boolean;
  min_age: number | null;
  max_age: number | null;
  format: ProgramFormat | null;
  instruction_languages: string[];
  influences: string[];
}

export type SessionInput = {
  name: string | null;
  start_date: string;
  end_date: string;
};

export interface ShowViewProps {
  // Tabs shell
  initialTab: string;
  onTabChange: (key: Key) => void;
  locationsCount: number;
  extractionPanel?: ReactNode;

  // Details tab
  values: ProgramDetailsValues;
  errors: Partial<Record<keyof ProgramDetailsValues, string | string[]>>;
  processing: boolean;
  supportedInfluences: string[];
  supportedLanguages: string[];
  supportedFormats: string[];
  photoSection: ReactNode;
  onFieldChange: <K extends keyof ProgramDetailsValues>(field: K, value: ProgramDetailsValues[K]) => void;
  onClearError: (field: keyof ProgramDetailsValues) => void;
  onDetailsSubmit: (e: FormEvent) => void;
  fieldOrigins: Record<string, "ai" | "admin">;
  touchedFields: string[];
  onRevertField: (field: string) => void;

  // Locations tab
  locationSearchPath: string;
  programLocations: AdminProgramsLocation[];
  onAddLocation: (result: MapboxSearchResult) => void;
  onRemoveLocation: (locationId: number) => void;

  // Availability tab
  isSessionBased: boolean;
  onAvailabilityModeChange: (sessionBased: boolean) => void;
  sessions: AdminProgramsSession[];
  onAddSession: (session: SessionInput, opts: { onSuccess: () => void }) => void;
  onRemoveSession: (sessionId: number) => void;
}

const sessionDateFormatter = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

function formatSessionRange(startDate: string, endDate: string): string {
  const start = sessionDateFormatter.format(new Date(`${startDate}T00:00:00Z`));
  const end = sessionDateFormatter.format(new Date(`${endDate}T00:00:00Z`));
  return `${start} – ${end}`;
}

export function ShowView({
  initialTab,
  onTabChange,
  locationsCount,
  extractionPanel,
  values,
  errors,
  processing,
  supportedInfluences,
  supportedLanguages,
  supportedFormats,
  photoSection,
  onFieldChange,
  onClearError,
  onDetailsSubmit,
  fieldOrigins,
  touchedFields,
  onRevertField,
  locationSearchPath,
  programLocations,
  onAddLocation,
  onRemoveLocation,
  isSessionBased,
  onAvailabilityModeChange,
  sessions,
  onAddSession,
  onRemoveSession,
}: ShowViewProps) {
  return (
    <>
      {extractionPanel}

      <Tabs defaultSelectedKey={initialTab} onSelectionChange={onTabChange}>
        <TabList orientation="horizontal" type="button-minimal">
          <Tab id="details" label="Details" />
          <Tab id="locations" label="Locations" badge={String(locationsCount)} />
          <Tab id="availability" label="Availability" />
        </TabList>

        <TabPanel id="details">
          <DetailsForm
            values={values}
            errors={errors}
            processing={processing}
            supportedInfluences={supportedInfluences}
            supportedLanguages={supportedLanguages}
            supportedFormats={supportedFormats}
            photoSection={photoSection}
            onFieldChange={onFieldChange}
            onClearError={onClearError}
            onSubmit={onDetailsSubmit}
            fieldOrigins={fieldOrigins}
            touchedFields={touchedFields}
            onRevertField={onRevertField}
          />
        </TabPanel>

        <TabPanel id="locations">
          <LocationsTab
            locationSearchPath={locationSearchPath}
            programLocations={programLocations}
            onAddLocation={onAddLocation}
            onRemoveLocation={onRemoveLocation}
          />
        </TabPanel>

        <TabPanel id="availability">
          <AvailabilityTab
            isSessionBased={isSessionBased}
            onAvailabilityModeChange={onAvailabilityModeChange}
            sessions={sessions}
            onAddSession={onAddSession}
            onRemoveSession={onRemoveSession}
          />
        </TabPanel>
      </Tabs>
    </>
  );
}

interface DetailsFormProps {
  values: ProgramDetailsValues;
  errors: Partial<Record<keyof ProgramDetailsValues, string | string[]>>;
  processing: boolean;
  supportedInfluences: string[];
  supportedLanguages: string[];
  supportedFormats: string[];
  photoSection: ReactNode;
  onFieldChange: <K extends keyof ProgramDetailsValues>(field: K, value: ProgramDetailsValues[K]) => void;
  onClearError: (field: keyof ProgramDetailsValues) => void;
  onSubmit: (e: FormEvent) => void;
  fieldOrigins: Record<string, "ai" | "admin">;
  touchedFields: string[];
  onRevertField: (field: string) => void;
}

function DetailsForm({
  values,
  errors,
  processing,
  supportedInfluences,
  supportedLanguages,
  supportedFormats,
  photoSection,
  onFieldChange,
  onClearError,
  onSubmit,
  fieldOrigins,
  touchedFields,
  onRevertField,
}: DetailsFormProps) {
  const aiBadge = (field: string) => (
    <AiSuggestedBadge field={field} origins={fieldOrigins} touched={touchedFields} onRevert={onRevertField} />
  );
  const errorOf = (field: keyof ProgramDetailsValues) => firstError(errors[field]);

  const languageDisplay = useMemo(
    () =>
      typeof Intl !== "undefined" && "DisplayNames" in Intl
        ? new Intl.DisplayNames(["en"], { type: "language" })
        : null,
    [],
  );
  const languageItems = supportedLanguages
    .map((code) => ({ id: code, label: languageDisplay?.of(code) ?? code.toUpperCase() }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const influenceItems = supportedInfluences.map((key) => ({ id: key, label: humanize(key) }));
  const formatItems = supportedFormats.map((key) => ({ id: key, label: humanize(key) }));

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-6 pt-8">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex flex-1 flex-col justify-center gap-0.5 self-stretch">
            <SectionHeader.Heading>Program details</SectionHeader.Heading>
            <SectionHeader.Subheading>Identity, age range, instruction, and photos.</SectionHeader.Subheading>
          </div>
          <SectionHeader.Actions>
            <Button type="submit" color="primary" size="sm" isDisabled={processing}>
              Save
            </Button>
          </SectionHeader.Actions>
        </SectionHeader.Group>
      </SectionHeader.Root>

      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
          <SectionLabel.Root size="sm" title="Name" className="max-lg:hidden" />
          <Input
            label="Name"
            value={values.name}
            onChange={(value) => {
              onFieldChange("name", value);
              onClearError("name");
            }}
            isRequired
            isInvalid={!!errors.name}
            hint={errorOf("name")}
            className="lg:label:hidden"
          />
        </div>

        <hr className="h-px w-full border-none bg-border-secondary" />

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
          <SectionLabel.Root
            size="sm"
            title={<>Tagline{aiBadge("tagline")}</>}
            description="A short hook (max 160 characters)."
          />
          <Input
            label="Tagline"
            value={values.tagline}
            onChange={(value) => {
              onFieldChange("tagline", value);
              onClearError("tagline");
            }}
            isInvalid={!!errors.tagline}
            hint={errorOf("tagline")}
            className="lg:label:hidden"
          />
        </div>

        <hr className="h-px w-full border-none bg-border-secondary" />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(200px,280px)_minmax(400px,768px)] lg:gap-8">
          <SectionLabel.Root
            size="sm"
            title={<>Description{aiBadge("description")}</>}
            description="Long-form details about the program."
          />
          <RichTextEditor
            value={values.description}
            onChange={(html) => onFieldChange("description", html)}
            placeholder="Describe the program — schedule, philosophy, who it's for…"
            isInvalid={!!errors.description}
            hint={errorOf("description")}
          />
        </div>

        <hr className="h-px w-full border-none bg-border-secondary" />

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
          <SectionLabel.Root
            size="sm"
            title={<>Format{aiBadge("format")}</>}
            description="How the program runs across the year. Required to publish."
          />
          <Select
            label="Format"
            placeholder="Select format"
            items={formatItems}
            selectedKey={values.format}
            onSelectionChange={(key) => {
              if (key === null) return;
              onFieldChange("format", key as ProgramFormat);
              onClearError("format");
            }}
            isInvalid={!!errors.format}
            hint={errorOf("format")}
          >
            {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
          </Select>
        </div>

        <hr className="h-px w-full border-none bg-border-secondary" />

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
          <SectionLabel.Root
            size="sm"
            title={
              <>
                Age range{aiBadge("min_age")}
                {aiBadge("max_age")}
              </>
            }
            description="The supported age window for participants."
          />
          <div className="grid grid-cols-2 gap-4">
            <InputNumber
              label="Min age"
              minValue={0}
              maxValue={99}
              value={values.min_age ?? Number.NaN}
              onChange={(value) => onFieldChange("min_age", Number.isNaN(value) ? null : value)}
              isInvalid={!!errors.min_age}
              hint={errorOf("min_age")}
            />
            <InputNumber
              label="Max age"
              minValue={0}
              maxValue={99}
              value={values.max_age ?? Number.NaN}
              onChange={(value) => onFieldChange("max_age", Number.isNaN(value) ? null : value)}
              isInvalid={!!errors.max_age}
              hint={errorOf("max_age")}
            />
          </div>
        </div>

        <hr className="h-px w-full border-none bg-border-secondary" />

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
          <SectionLabel.Root
            size="sm"
            title={<>Instruction languages{aiBadge("instruction_languages")}</>}
            description="Languages the program teaches in."
          />
          <MultiSelect
            label="Languages"
            placeholder="Select languages"
            items={languageItems}
            selectedKeys={new Set(values.instruction_languages)}
            onSelectionChange={(keys) => {
              const next = keys === "all" ? supportedLanguages : Array.from(keys as Set<string>);
              onFieldChange("instruction_languages", next);
            }}
          >
            {(item) => <MultiSelect.Item id={item.id}>{item.label}</MultiSelect.Item>}
          </MultiSelect>
        </div>

        <hr className="h-px w-full border-none bg-border-secondary" />

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
          <SectionLabel.Root
            size="sm"
            title={<>Influences{aiBadge("influences")}</>}
            description="Pedagogical traditions this program draws on."
          />
          <MultiSelect
            label="Influences"
            placeholder="Select influences"
            items={influenceItems}
            selectedKeys={new Set(values.influences)}
            onSelectionChange={(keys) => {
              const next = keys === "all" ? supportedInfluences : Array.from(keys as Set<string>);
              onFieldChange("influences", next);
            }}
          >
            {(item) => <MultiSelect.Item id={item.id}>{item.label}</MultiSelect.Item>}
          </MultiSelect>
        </div>

        <hr className="h-px w-full border-none bg-border-secondary" />

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
          <SectionLabel.Root
            size="sm"
            title={<>Drop-off available{aiBadge("drop_off_available")}</>}
            description="Parents can drop children off and leave."
          />
          <Toggle
            isSelected={values.drop_off_available}
            onChange={(value) => onFieldChange("drop_off_available", value)}
            label="Allow drop-off"
          />
        </div>

        <hr className="h-px w-full border-none bg-border-secondary" />

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
          <SectionLabel.Root
            size="sm"
            title={<>Community program{aiBadge("community_program")}</>}
            description="Open to whole families, not just children."
          />
          <Toggle
            isSelected={values.community_program}
            onChange={(value) => onFieldChange("community_program", value)}
            label="Whole-family program"
          />
        </div>

        <hr className="h-px w-full border-none bg-border-secondary" />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(200px,280px)_minmax(400px,768px)] lg:gap-8">
          <SectionLabel.Root size="sm" title="Photos" description="At least one. First by position is the cover." />
          {photoSection}
        </div>
      </div>

      <SectionFooter.Root>
        <SectionFooter.Actions>
          <Button type="submit" color="primary" size="sm" isDisabled={processing}>
            Save
          </Button>
        </SectionFooter.Actions>
      </SectionFooter.Root>
    </form>
  );
}

interface LocationsTabProps {
  locationSearchPath: string;
  programLocations: AdminProgramsLocation[];
  onAddLocation: (result: MapboxSearchResult) => void;
  onRemoveLocation: (locationId: number) => void;
}

function LocationsTab({ locationSearchPath, programLocations, onAddLocation, onRemoveLocation }: LocationsTabProps) {
  return (
    <div className="flex flex-col gap-6 pt-8">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex flex-1 flex-col justify-center gap-0.5 self-stretch">
            <SectionHeader.Heading>Locations</SectionHeader.Heading>
            <SectionHeader.Subheading>
              Concrete addresses through to cities. Programs can move between locations.
            </SectionHeader.Subheading>
          </div>
        </SectionHeader.Group>
      </SectionHeader.Root>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
        <SectionLabel.Root size="sm" title="Add a location" description="Search Mapbox for a place to link." />
        <MapboxSearch
          searchPath={locationSearchPath}
          label="Search"
          placeholder="e.g. Hvar, 10 Downing Street, Bangkok..."
          onSelect={onAddLocation}
        />
      </div>

      <hr className="h-px w-full border-none bg-border-secondary" />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(200px,280px)_minmax(400px,768px)] lg:gap-8">
        <SectionLabel.Root size="sm" title="Linked locations" description="Locations associated with this program." />
        {programLocations.length === 0 ? (
          <p className="rounded-md border border-secondary border-dashed px-4 py-6 text-center text-sm text-tertiary">
            No locations yet. Search above to add one.
          </p>
        ) : (
          <ul className="overflow-hidden rounded-md border border-secondary">
            {programLocations.map((link) => {
              const location = link.location as {
                id: number;
                name: string;
                full_address: string | null;
                country_code: string;
              };
              return (
                <li
                  key={link.id}
                  className="flex items-center justify-between gap-3 border-secondary border-b px-3 py-2 last:border-b-0"
                >
                  <div className="flex flex-col">
                    <span className="font-medium text-primary text-sm">{location.name}</span>
                    <span className="text-tertiary text-xs">{location.full_address ?? location.country_code}</span>
                  </div>
                  <Button color="tertiary-destructive" size="sm" onClick={() => onRemoveLocation(location.id)}>
                    Remove
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

interface AvailabilityTabProps {
  isSessionBased: boolean;
  onAvailabilityModeChange: (sessionBased: boolean) => void;
  sessions: AdminProgramsSession[];
  onAddSession: (session: SessionInput, opts: { onSuccess: () => void }) => void;
  onRemoveSession: (sessionId: number) => void;
}

function AvailabilityTab({
  isSessionBased,
  onAvailabilityModeChange,
  sessions,
  onAddSession,
  onRemoveSession,
}: AvailabilityTabProps) {
  return (
    <div className="flex flex-col gap-6 pt-8">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex flex-1 flex-col justify-center gap-0.5 self-stretch">
            <SectionHeader.Heading>Availability</SectionHeader.Heading>
            <SectionHeader.Subheading>
              Whether this program runs year-round or only on scheduled session dates.
            </SectionHeader.Subheading>
          </div>
        </SectionHeader.Group>
      </SectionHeader.Root>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
        <SectionLabel.Root
          size="sm"
          title="Availability mode"
          description="Session-based programs run only on the dates you add below; anytime programs always show for any “When”."
        />
        <Toggle isSelected={isSessionBased} onChange={onAvailabilityModeChange} label="Runs in scheduled sessions" />
      </div>

      {isSessionBased && (
        <>
          <hr className="h-px w-full border-none bg-border-secondary" />
          <SessionsSection sessions={sessions} onAddSession={onAddSession} onRemoveSession={onRemoveSession} />
        </>
      )}
    </div>
  );
}

interface SessionsSectionProps {
  sessions: AdminProgramsSession[];
  onAddSession: (session: SessionInput, opts: { onSuccess: () => void }) => void;
  onRemoveSession: (sessionId: number) => void;
}

function SessionsSection({ sessions, onAddSession, onRemoveSession }: SessionsSectionProps) {
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState<DateValue | null>(null);
  const [endDate, setEndDate] = useState<DateValue | null>(null);

  const canAdd = startDate != null && endDate != null && startDate.compare(endDate) <= 0;

  const addSession = (e: FormEvent) => {
    e.preventDefault();
    if (!startDate || !endDate) return;
    onAddSession(
      { name: name.trim() || null, start_date: startDate.toString(), end_date: endDate.toString() },
      {
        onSuccess: () => {
          setName("");
          setStartDate(null);
          setEndDate(null);
        },
      },
    );
  };

  const minDate = today(getLocalTimeZone());

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
        <SectionLabel.Root size="sm" title="Add a session" description="A named date range the program runs in." />
        <form onSubmit={addSession} className="flex flex-col gap-4">
          <Input label="Name (optional)" value={name} onChange={setName} placeholder="e.g. Summer term" />
          <div className="grid grid-cols-2 gap-4">
            <InputDate label="Start date" value={startDate} onChange={setStartDate} minValue={minDate} />
            <InputDate label="End date" value={endDate} onChange={setEndDate} minValue={startDate ?? minDate} />
          </div>
          <div>
            <Button type="submit" color="primary" size="sm" isDisabled={!canAdd}>
              Add session
            </Button>
          </div>
        </form>
      </div>

      <hr className="h-px w-full border-none bg-border-secondary" />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(200px,280px)_minmax(400px,768px)] lg:gap-8">
        <SectionLabel.Root size="sm" title="Scheduled sessions" description="Date ranges this program runs in." />
        {sessions.length === 0 ? (
          <p className="rounded-md border border-secondary border-dashed px-4 py-6 text-center text-sm text-tertiary">
            No sessions yet. Add one above.
          </p>
        ) : (
          <ul className="overflow-hidden rounded-md border border-secondary">
            {sessions.map((session) => (
              <li
                key={session.id}
                className="flex items-center justify-between gap-3 border-secondary border-b px-3 py-2 last:border-b-0"
              >
                <div className="flex flex-col">
                  <span className="font-medium text-primary text-sm">
                    {formatSessionRange(session.start_date, session.end_date)}
                  </span>
                  {session.name && <span className="text-tertiary text-xs">{session.name}</span>}
                </div>
                <Button color="tertiary-destructive" size="sm" onClick={() => onRemoveSession(session.id)}>
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
